/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import BoxManager from '../Box/BoxManager';
import { defaultValue } from './CollectionList';

const CollectionListManager: ComponentManagerFn<'CollectionList'> = (
  platform: Platform,
) => {
  const boxManager = BoxManager(platform, 'Box');
  return {
    ...baseComponentManager<'CollectionList'>(platform, 'CollectionList'),
    type: 'CollectionList',
    name: 'Collection List',
    isChildAllowed: (page, item, childType) =>
      (['Box'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      ...boxManager.props,
      collection: {
        label: 'Collection',
        type: 'expressionOnly',
        returnType: () => 'array',
      },
      name: {
        label: 'Collection Name',
        type: 'string',
      },
    },
    isPropertyVisible: (property, itemData, context) => {
      switch (property) {
        case 'alignSelf':
        case 'border':
        case 'borderRadius':
        case 'boxShadow':
        case 'color':
        case 'component':
        case 'display':
        case 'fontSize':
        case 'fontStyle':
        case 'fontWeight':
        case 'overflow':
        case 'showOnlyOnHoverItemIds':
          return false;
        case 'collection':
        case 'name':
          return true;
        default:
          return boxManager.isPropertyVisible(property, itemData, context);
      }
    },
    getDisplayText: (item) => item.props.name,
    icon: { iconName: 'boxes' },
    category: 'Data Containers',
    canHaveChildren: () => true,
    noOverlay: true,
    description: `**Overview**<br/>
The Collection List is a lightweight "repeater" component. It takes a raw JSON array (list of data) and renders a specific UI layout for every item in that array.
While components like the Box List or Data Grid are designed to work with formal Data Stores, the Collection List is designed for Ad-Hoc Data. Use it when you have a simple array—such as a list of tags, a hardcoded menu, or a specific property from an API response—and you want to display it without creating a full Data Store.

**1. Data Source (The Array)**<br/>
The component relies on a single expression to generate its content.
* **Collection:** An expression that must resolve to an Array ([]).
* **Examples:**
    * \`['Red', 'Green', 'Blue']\` (Hardcoded string array)
    * \`$.apiResponse.tags\` (Array from an API result)
    * \`$.user.permissions\` (Array from a session variable)
* **Name:** A descriptive label used to identify this collection in the designer tree (e.g., "Tags List" or "User Permissions").

**2. The "Item Template" (The Box)**<br/>
The Collection List is a container, but it allows only one specific type of direct child: a Box.
* **How it Works:** The child Box acts as the "Template."
    * You design the Box once (adding styling, icons, or text).
    * The Collection List automatically repeats that Box for every entry in your array.
* **Layout:** Since it extends the Box architecture, you can control the layout of the list itself (e.g., arranging items vertically in a column or horizontally in a row) using standard Flexbox properties (Direction, Align, Justify).

**3. Usage Comparison: When to use what?**<br/>
* **Data Source:** Box List uses a Data Store (Backend Table); Collection List uses an Expression / Array (JSON).
* **Editing:** Box List supports CRUD (Edit/Delete); Collection List is Read-Only / Display.
* **Best For:** Box List is for Database Records, Task Lists; Collection List is for Tags, Simple Menus, Read-only Lists.
* **Performance:** Box List is optimized for large sets; Collection List is best for small-to-medium arrays.

**4. Context Access**<br/>
Inside the child Box, you can typically access the specific data of the current iteration item (e.g., "Red" or "Green") using the platform's iteration context syntax (often \`$.current\` or \`$.item\`, depending on your specific version's standard).`,
  };
};

export default CollectionListManager;
